import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EnrollmentOptions from './EnrollmentOptions';
import { useLazyQuery } from '@apollo/client';
import gql from 'graphql-tag';

interface Option {
  label: string;
  value: string;
}

interface EnrollmentManagerProps {
  onDataChange: (data: Option[]) => void;
  currentList: Option[];
  otherList: Option[];
}

const typeOptions: Option[] = [
  { label: 'County', value: '(type:WzEzMTA3MjAsMCwxLDFd) ' },
  { label: 'BoardCertification', value: '(type:WzEzMTA3MjAsMCwxLDJd) ' },
  { label: 'DoctorSpecialty', value: '(type:WzEzMTA3MjAsMCwxLDNd) ' },
  { label: 'MemberType', value: '(type:WzEzMTA3MjAsMCwxLDVd) ' },
  { label: 'DoctorType', value: '(type:WzEzMTA3MjAsMCwxLDZd) ' },
  { label: 'DoctorGroup', value: '(type:WzEzMTA3MjAsMCwxLDdd) ' },
];

const ENROLLMENT_QUERY = gql`
  query GetEnrollments($type: String!) {
    enrollments(query: $type) {
      edges {
        cursor
        node {
          id
          ... on Enrollment {
            name
          }
        }
      }
    }
  }
`;

function EnrollmentManager({ onDataChange, currentList, otherList }: EnrollmentManagerProps) {
  const [selectedType, setSelectedType] = useState<string>();
  const [selectedEnrollments, setSelectedEnrollments] = useState<Option[]>(currentList);

  const [getEnrollments, { data, loading, error }] = useLazyQuery(ENROLLMENT_QUERY, {
    variables: { type: selectedType },
    onError(err) {
      console.error('GraphQL Query Error:', err.message);
    },
  });

  useEffect(() => {
    if (selectedType) {
      getEnrollments({ variables: { type: selectedType } });
    }
  }, [selectedType, getEnrollments]);

  const enrollmentOptions = useMemo(() => {
    const allEnrollments =
      data?.enrollments.edges.map(({ node }: any) => ({
        label: node.name,
        value: node.id,
      })) || [];

    return allEnrollments.filter(
      (enrollment: any) =>
        !currentList.some(item => item.value === enrollment.value) &&
        !otherList.some(item => item.value === enrollment.value)
    );
  }, [data, currentList, otherList]);

  const handleEnrollmentSelect = useCallback(
    (enrollment: Option | null) => {
      if (enrollment) {
        setSelectedEnrollments(selectedEnrollments => {
          if (
            !selectedEnrollments.find(item => item.value === enrollment.value) &&
            !otherList.find(item => item.value === enrollment.value)
          ) {
            const next = [...selectedEnrollments, enrollment];
            onDataChange(next);
            return next;
          }

          return selectedEnrollments;
        });
      }
    },
    [otherList, onDataChange]
  );

  const handleEnrollmentDeSelect = useCallback((value: string) => {
    setSelectedEnrollments(prev => prev.filter(enrollment => enrollment.value !== value));
  }, []);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <EnrollmentOptions
        typeOptions={typeOptions}
        enrollmentOptions={enrollmentOptions}
        onTypeChange={setSelectedType}
        onEnrollmentSelect={handleEnrollmentSelect}
      />
      <Paper sx={{ maxHeight: 200, overflow: 'auto' }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <List>
            {selectedEnrollments.map((enrollment, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleEnrollmentDeSelect(enrollment.value)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={enrollment.label} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
      {error && <Typography sx={{ color: 'red' }}>Error: {error.message}</Typography>}
    </Box>
  );
}

export default EnrollmentManager;
